// src/utils/siteChecks.js
//
// Deterministic (non-AI) checks comparing placed equipment's catalog
// `requirements` (free text like "Min 11ft Ceiling", "220V", "Concrete
// Anchor") against the site conditions entered on the Export page.
//
// This is intentionally NOT part of the AI call. A ceiling height check is
// just "is 10 < 11" — there's no reason to hand that kind of fact to an
// LLM and hope it reasons correctly every time. computeSiteChecks() does
// the actual comparison here in plain JS; the AI summary later just turns
// the results into a friendly paragraph. If this file has a bug, at least
// it's a bug you can read and fix — not a hallucination buried in a prompt.
import { CATALOG } from '../data/catalog';

// Pulls a number out of patterns like "Min 11ft Ceiling" or "Min 24ft Bay
// Depth". Returns null if this requirement string doesn't match — most
// requirements (e.g. "220V", "Concrete Anchor") won't, and that's fine.
const parseMinFt = (text, keyword) => {
  const re = new RegExp(`Min\\s*(\\d+(?:\\.\\d+)?)\\s*ft\\s*${keyword}`, 'i');
  const match = text.match(re);
  return match ? parseFloat(match[1]) : null;
};

// Returns { flags, notes }.
//   flags — real problems worth calling out clearly (a hard number doesn't
//           clear the requirement).
//   notes — lower-stakes context always worth mentioning (anchoring needed,
//           220V needed, or a bay-depth requirement that fits ONE way but
//           not both, so orientation matters).
export function computeSiteChecks(equipment, form, shop) {
  const flags = [];
  const notes = [];
  const ceilingHeight = parseFloat(form.ceilingHeight) || null;
  const shopMax = Math.max(shop.w, shop.h);
  const shopMin = Math.min(shop.w, shop.h);

  // Track which catalog types we've already flagged so 6 identical 2-post
  // lifts don't produce the same "needs 11ft ceiling" line 6 times.
  const seenCeiling = new Set();
  const seenDepth = new Set();
  const seenAnchor = new Set();
  let needs220 = false;

  equipment.forEach(item => {
    const cat = CATALOG.find(c => c.id === item.id);
    if (!cat) return;
    const reqs = cat.requirements || [];
    const name = item.label || cat.name;

    reqs.forEach(req => {
      if (req.includes('220V')) needs220 = true;

      const minCeiling = parseMinFt(req, 'Ceiling');
      if (minCeiling !== null && ceilingHeight !== null && !seenCeiling.has(cat.id)) {
        seenCeiling.add(cat.id);
        if (ceilingHeight < minCeiling) {
          flags.push(`${name} needs at least ${minCeiling}ft of ceiling clearance, but the site is listed at ${ceilingHeight}ft.`);
        }
      }

      const minDepth = parseMinFt(req, 'Bay Depth');
      if (minDepth !== null && !seenDepth.has(cat.id)) {
        seenDepth.add(cat.id);
        // Conservative check: only a real problem if NEITHER shop dimension
        // clears it. If one direction clears but not the other, that's a
        // placement/orientation question, not a guaranteed failure.
        if (shopMax < minDepth) {
          flags.push(`${name} needs ${minDepth}ft of bay depth, which doesn't fit either direction of this ${shop.w} x ${shop.h}ft shop.`);
        } else if (shopMin < minDepth) {
          notes.push(`${name} needs ${minDepth}ft of bay depth — double check its placement orientation allows for that.`);
        }
      }

      if ((req.includes('Concrete Anchor') || req.includes('In-Ground Installation')) && !seenAnchor.has(cat.id)) {
        seenAnchor.add(cat.id);
        notes.push(`${name} requires anchoring — confirm the slab (currently listed as ${form.concreteThickness || 'unspecified'}in, "${form.concreteCondition || 'unspecified'}" condition) can support it.`);
      }
    });
  });

  if (needs220) notes.push('Some equipment requires 220V service — confirm electrical panel capacity before installation day.');

  return { flags, notes };
}